import type { ConfigPapel, Formato, Moldura as TipoMoldura, Paleta } from '@miolos/core';
import { medirPagina, molduraDaPagina } from '@miolos/core';

interface Props {
  tipo: TipoMoldura;
  paleta: Paleta;
  formato: Formato;
  /** Lado da encadernação: a moldura respeita a margem do vinco. */
  lado: 'ESQUERDO' | 'DIREITO';
  /** Orientação e margem escolhidas no assistente. */
  papel?: ConfigPapel;
  /** Cor do traço escolhida. Nula = a cor de destaque do tema. */
  corEscolhida?: string | null;
  /** Espessura do traço, em milímetros. */
  espessuraMm?: number;
}

/**
 * Moldura decorativa das páginas de dia, de mês e dos cadernos.
 *
 * O retângulo vem de `molduraDaPagina`, no motor: é geometria de papel, medida
 * em milímetros e conferida nos testes. Aqui só se desenha — traço fino, na cor
 * escolhida (ou na de destaque do tema), no espaço entre o conteúdo e a borda da
 * folha.
 */
const ESPESSURA_PADRAO_MM = 0.35;
/** Comprimento das marcas em L do estilo "Cantos". */
const MARCA_CANTO_MM = 14;

export function Moldura({
  tipo,
  paleta,
  formato,
  lado,
  papel,
  corEscolhida,
  espessuraMm,
}: Props) {
  const medidas = medirPagina(formato, {
    orientacao: papel?.orientacao,
    ladoLombada: papel?.ladoLombada,
    margemLombadaMm: papel?.margemLombadaMm,
    lado,
  });

  const caixa = molduraDaPagina(medidas, tipo);
  if (!caixa) return null;

  const { xMm: x, yMm: y, larguraMm: largura, alturaMm: altura } = caixa;
  const espessura = espessuraMm ?? ESPESSURA_PADRAO_MM;

  const cor = corEscolhida ?? paleta.destaque;
  const traco = {
    fill: 'none',
    stroke: cor,
    strokeWidth: espessura,
    opacity: 0.85,
  } as const;

  const base: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 901,
    pointerEvents: 'none',
  };

  /** Traça as quatro marcas em L dos cantos, cada uma com os dois lados. */
  const cantos = () => {
    const marcas: Array<{ x: number; y: number; dx: number; dy: number }> = [
      { x, y, dx: 1, dy: 1 },
      { x: x + largura, y, dx: -1, dy: 1 },
      { x, y: y + altura, dx: 1, dy: -1 },
      { x: x + largura, y: y + altura, dx: -1, dy: -1 },
    ];

    return marcas.map((marca, i) => (
      <path
        key={i}
        d={`M ${marca.x} ${marca.y + marca.dy * MARCA_CANTO_MM} L ${marca.x} ${marca.y} L ${marca.x + marca.dx * MARCA_CANTO_MM} ${marca.y}`}
        {...traco}
      />
    ));
  };

  return (
    <div style={base}>
      <svg
        viewBox={`0 0 ${medidas.larguraMm} ${medidas.alturaMm}`}
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      >
        {tipo === 'FILETE' ? (
          <rect x={x} y={y} width={largura} height={altura} rx="2" {...traco} />
        ) : null}

        {tipo === 'FILETE_DUPLO' ? (
          <>
            <rect x={x} y={y} width={largura} height={altura} rx="2" {...traco} />
            <rect
              x={x + 1.6}
              y={y + 1.6}
              width={largura - 3.2}
              height={altura - 3.2}
              rx="2"
              {...traco}
              opacity={0.55}
            />
          </>
        ) : null}

        {tipo === 'CANTOS' ? cantos() : null}

        {tipo === 'TRACEJADA' ? (
          <rect
            x={x}
            y={y}
            width={largura}
            height={altura}
            rx="2"
            {...traco}
            strokeDasharray="3 2.4"
            opacity={0.7}
          />
        ) : null}

        {tipo === 'ARCO' ? (
          <>
            {/*
              O arco do alto passa por cima da linha do quadro: um semicírculo
              apoiado no centro da aresta superior, com o mesmo traço.
            */}
            <path
              d={`M ${x + largura / 2 - 12} ${y} A 12 9 0 0 1 ${x + largura / 2 + 12} ${y}`}
              {...traco}
            />
            <rect x={x} y={y} width={largura} height={altura} rx="8" {...traco} />
          </>
        ) : null}
      </svg>
    </div>
  );
}
